import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useCallback, useMemo, useState } from "react"
import { FormProvider, useForm, useWatch } from "react-hook-form"

import { useCancelGlTransaction } from "../../hooks/useCancelGlTransaction"
import { useGlTransactionFileUpload } from "../../hooks/useGlTransactionFileUpload"
import { useReverseGlTransaction } from "../../hooks/useReverseGlTransaction"
import { useUpdateGlTransaction } from "../../hooks/useUpdateGlTransaction"
import { type GlTransactionFormValues, glTransactionFormSchema } from "../../schemas/glTransactionForm"
import { type GlTransactionDialogMode } from "../../types/dialog"
import { toGlTransactionUpdateInput } from "../../utils/glTransactionFormUtils"
import { DocumentAttachmentsSection } from "../DocumentAttachmentsSection"
import { GlTransactionDateSection } from "../GlTransactionDateSection"
import { RecurringSection } from "../RecurringSection"
import { TransactionLinesSection } from "../TransactionLinesSection"

import { EditGlTransactionDialogActions } from "./EditGlTransactionDialogActions"

import { CancelDialog } from "@/components/common/Modals"
import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { AerosTextField } from "@/components/UiKit"
import { FormCheckbox } from "@/components/UiKit/FormControls/FormCheckbox"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import type { GlTransaction } from "@/graphql/codegen/graphql"
import { GlTransactionRepeatByUnit, GlTransactionStatus } from "@/graphql/codegen/graphql"
import { formatGlAccountLabel } from "@/screens/Accounting/subfeatures/ChartOfAccounts/utils/formatGlAccountLabel"

const getDialogTitle = (mode: GlTransactionDialogMode, isPosted: boolean) => {
  if (mode === "reverse") return "Reverse Transaction"
  if (mode === "cancel") return "Cancel Transaction"
  if (mode === "view") return "View General Ledger Entry"
  if (isPosted) return "View / Edit Posted Entry"
  return "Edit General Ledger Entry"
}

export interface EditGlTransactionDialogFormProps {
  glTransaction: GlTransaction
  mode: GlTransactionDialogMode
  onClose: () => void
  onSuccess?: () => void
}

export const EditGlTransactionDialogForm: FC<EditGlTransactionDialogFormProps> = ({
  glTransaction,
  mode,
  onClose,
  onSuccess,
}) => {
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const [isRecurringCancelWarningOpen, setIsRecurringCancelWarningOpen] = useState(false)
  const [isRecurringEditConfirmOpen, setIsRecurringEditConfirmOpen] = useState(false)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [reverseReason, setReverseReason] = useState("")
  const [cancelReason, setCancelReason] = useState("")
  const { updateGlTransaction, isUpdating } = useUpdateGlTransaction()
  const { uploadFilesForTransactions, isUploading } = useGlTransactionFileUpload()
  const { reverseGlTransaction, isReversing } = useReverseGlTransaction()
  const { cancelGlTransaction, isCancelling } = useCancelGlTransaction()

  const { status } = glTransaction
  const isPosted = status === GlTransactionStatus.Posted
  const isReversed = status === GlTransactionStatus.Reversed
  const isCancelled = status === GlTransactionStatus.Cancelled
  const isReadOnly = isReversed || isCancelled
  const isExistingRecurring = Boolean(glTransaction.recurringId)

  const isReverseMode = mode === "reverse"
  const isCancelMode = mode === "cancel"
  const isViewMode = mode === "view" || (mode === "edit" && isReadOnly)
  const allDisabled = mode !== "edit" || isPosted || isReadOnly

  const isConfirming = isReversing || isCancelling

  const defaultValues = useMemo<GlTransactionFormValues>(
    () => ({
      transactionLines: glTransaction.glTransactionRows.map((row) => ({
        glTransactionRowId: row.glTransactionRowId,
        type: row.type,
        glAccountId: row.glAccountId,
        amount: row.amount,
      })),
      transactionDate: glTransaction.transactionDate,
      description: glTransaction.description,
      note: glTransaction.note ?? "",
      isRecurring: isExistingRecurring,
      isExistingRecurring,
      repeatEndDate: "",
      repeatEvery: 1,
      repeatByUnit: GlTransactionRepeatByUnit.Month,
    }),
    [glTransaction, isExistingRecurring]
  )

  const initialSelectedOptions = useMemo(
    () =>
      glTransaction.glTransactionRows.reduce<Record<number, { value: string; label: string }>>((acc, row, i) => {
        if (row.glAccount) {
          acc[i] = {
            value: row.glAccount.glAccountId!,
            label: formatGlAccountLabel(row.glAccount),
          }
        }
        return acc
      }, {}),
    [glTransaction]
  )

  const methods = useForm<GlTransactionFormValues>({
    resolver: zodResolver(glTransactionFormSchema),
    defaultValues,
    mode: "onChange",
  })

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty, isSubmitting },
  } = methods

  const isRecurring = useWatch({ control, name: "isRecurring" })

  const canSubmit = isValid && (isDirty || pendingFiles.length > 0) && !isSubmitting && !isUpdating && !isUploading

  const canConfirm = (() => {
    if (isReverseMode) return reverseReason.trim().length > 0
    if (isCancelMode) return true
    return canSubmit
  })()

  const reasonField = (() => {
    if (isReverseMode || (isViewMode && isReversed)) {
      return {
        label: "Reversal Reason",
        value: isReverseMode ? reverseReason : (glTransaction.reason ?? ""),
        onChange: isReverseMode ? setReverseReason : undefined,
        editable: isReverseMode,
        required: isReverseMode,
        testId: "reversal-reason",
      }
    }
    if (isCancelMode || (isViewMode && isCancelled)) {
      return {
        label: "Cancellation Reason",
        value: isCancelMode ? cancelReason : (glTransaction.reason ?? ""),
        onChange: isCancelMode ? setCancelReason : undefined,
        editable: isCancelMode,
        required: false,
        testId: "cancellation-reason",
      }
    }
    return null
  })()

  const handleFileAdd = useCallback((file: File) => {
    setPendingFiles((prev) => [...prev, file])
  }, [])

  const handleFileRemove = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const executeUpdate = useCallback(
    async (formValues: GlTransactionFormValues) => {
      const input = toGlTransactionUpdateInput(formValues, glTransaction.glTransactionId)
      const success = await updateGlTransaction(input)
      if (success) {
        if (pendingFiles.length > 0) {
          await uploadFilesForTransactions(pendingFiles, [glTransaction.glTransactionId])
        }
        onSuccess?.()
        onClose()
      }
    },
    [glTransaction, updateGlTransaction, uploadFilesForTransactions, pendingFiles, onSuccess, onClose]
  )

  const handleFormSubmit = useCallback(
    async (formValues: GlTransactionFormValues) => {
      if (isExistingRecurring) {
        setIsRecurringEditConfirmOpen(true)
        return
      }
      await executeUpdate(formValues)
    },
    [isExistingRecurring, executeUpdate]
  )

  const handleRecurringEditConfirm = useCallback(async () => {
    setIsRecurringEditConfirmOpen(false)
    await executeUpdate(methods.getValues())
  }, [executeUpdate, methods])

  const executeCancelAction = useCallback(async () => {
    const success = await cancelGlTransaction({
      glTransactionId: glTransaction.glTransactionId,
      cancellationReason: cancelReason.trim() || "",
    })
    if (success) {
      onSuccess?.()
      onClose()
    }
  }, [glTransaction.glTransactionId, cancelReason, cancelGlTransaction, onSuccess, onClose])

  const handleAction = useCallback(async () => {
    if (isReverseMode) {
      const success = await reverseGlTransaction({
        glTransactionId: glTransaction.glTransactionId,
        reverseReason: reverseReason.trim(),
      })
      if (success) {
        onSuccess?.()
        onClose()
      }
      return
    }

    if (glTransaction.recurringId) {
      setIsRecurringCancelWarningOpen(true)
      return
    }

    await executeCancelAction()
  }, [
    isReverseMode,
    glTransaction.glTransactionId,
    glTransaction.recurringId,
    reverseReason,
    reverseGlTransaction,
    executeCancelAction,
    onSuccess,
    onClose,
  ])

  const handleDirtyClose = useCallback(() => {
    if (isDirty || pendingFiles.length > 0) {
      setIsCancelDialogOpen(true)
    } else {
      onClose()
    }
  }, [isDirty, pendingFiles.length, onClose])

  const dialogTitle = getDialogTitle(mode, isPosted)

  if (isCancelDialogOpen) {
    return <CancelDialog onClose={() => setIsCancelDialogOpen(false)} onConfirm={onClose} />
  }

  return (
    <>
      {isRecurringCancelWarningOpen && (
        <ConfirmationDialog
          title="You're cancelling a repeating transaction"
          content="Cancelling this transaction will apply to this occurrence only and won't affect other repeating transactions."
          closeLabel='Cancel'
          confirmLabel='Continue'
          isWarning
          onClose={() => setIsRecurringCancelWarningOpen(false)}
          onConfirm={executeCancelAction}
        />
      )}
      {isRecurringEditConfirmOpen && (
        <ConfirmationDialog
          title='Edit Recurring Transaction'
          content='This transaction is part of a recurring series. Only this occurrence will be modified.'
          closeLabel='Cancel'
          confirmLabel='Edit this occurrence'
          isWarning
          onClose={() => setIsRecurringEditConfirmOpen(false)}
          onConfirm={handleRecurringEditConfirm}
        />
      )}
      <Dialog
        open
        onClose={isViewMode ? onClose : handleDirtyClose}
        maxWidth='md'
        fullWidth
        data-testid='gl-transaction.edit-dialog'
      >
        <DialogHeader title={dialogTitle} onClose={isViewMode ? onClose : handleDirtyClose} />
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <DialogContent className='flex flex-col gap-6 pt-4'>
              <TransactionLinesSection disabled={allDisabled} initialSelectedOptions={initialSelectedOptions} />

              <Box className='grid grid-cols-3 items-start gap-4'>
                <GlTransactionDateSection
                  disabled={allDisabled}
                  showAlerts={!allDisabled}
                  testIdPrefix='gl-transaction.edit-dialog'
                />
                <Box className='pt-5'>
                  <FormCheckbox
                    control={control}
                    name='isRecurring'
                    label='Recurring Event'
                    disabled={allDisabled || isExistingRecurring}
                    data-testid='gl-transaction.edit-dialog.recurring-checkbox'
                  />
                </Box>
              </Box>

              {isRecurring && !isExistingRecurring && <RecurringSection />}

              <FormTextField
                className='flex-1'
                control={control}
                name='description'
                label='Description'
                required
                disabled={allDisabled}
                slotProps={{ htmlInput: { maxLength: 30 } }}
                data-testid='gl-transaction.edit-dialog.description'
              />

              <FormTextField
                control={control}
                name='note'
                label='Notes'
                multiline
                rows={3}
                disabled={mode !== "edit" || isReadOnly}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                data-testid='gl-transaction.edit-dialog.note'
              />

              {reasonField && (
                <AerosTextField
                  label={reasonField.label}
                  value={reasonField.value}
                  onChange={reasonField.onChange}
                  disabled={!reasonField.editable}
                  required={reasonField.required}
                  size='small'
                  multiline
                  rows={3}
                  data-testid={`gl-transaction.edit-dialog.${reasonField.testId}`}
                />
              )}

              {mode === "edit" && !isReadOnly && (
                <DocumentAttachmentsSection files={pendingFiles} onAdd={handleFileAdd} onRemove={handleFileRemove} />
              )}
            </DialogContent>

            <DialogActions className='px-6 pb-6'>
              <EditGlTransactionDialogActions
                mode={isViewMode ? "view" : mode}
                canConfirm={canConfirm}
                isConfirming={isConfirming}
                onClose={onClose}
                onDirtyClose={handleDirtyClose}
                onAction={handleAction}
              />
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
    </>
  )
}
